import { useState, useMemo, useCallback } from "react";
import { Video, TagFilters, FilterResult, TagHierarchy } from "../types/api";
import { filterVideosByTags, buildTagHierarchy } from "../utils/searchUtils";

interface UseVideoFiltersProps {
  videos: Video[];
}

interface UseVideoFiltersReturn {
  tagFilters: TagFilters;
  filterResult: FilterResult;
  toggleTagValue: (tagKey: string, value: string) => void;
  clearAllFilters: () => void;
  clearTagKeyFilters: (tagKey: string) => void;
  hasActiveFilters: boolean;
  availableTags: TagHierarchy;
}

export function useVideoFilters({
  videos,
}: UseVideoFiltersProps): UseVideoFiltersReturn {
  const [tagFilters, setTagFilters] = useState<TagFilters>({});

  // Build available tags hierarchy
  const availableTags = useMemo(() => {
    return buildTagHierarchy(videos);
  }, [videos]);

  // Toggle a specific tag value
  const toggleTagValue = useCallback((tagKey: string, value: string) => {
    setTagFilters((prev) => {
      const newFilters = { ...prev };

      if (!newFilters[tagKey]) {
        newFilters[tagKey] = new Set();
      } else {
        // Create a new Set to ensure state update
        newFilters[tagKey] = new Set(newFilters[tagKey]);
      }

      if (newFilters[tagKey].has(value)) {
        newFilters[tagKey].delete(value);
        // Remove empty filter sets
        if (newFilters[tagKey].size === 0) {
          delete newFilters[tagKey];
        }
      } else {
        newFilters[tagKey].add(value);
      }

      return newFilters;
    });
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setTagFilters({});
  }, []);

  // Clear filters for a specific tag key
  const clearTagKeyFilters = useCallback((tagKey: string) => {
    setTagFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[tagKey];
      return newFilters;
    });
  }, []);

  // Compute filtered results
  const filterResult = useMemo((): FilterResult => {
    const filteredVideos = filterVideosByTags(videos, tagFilters);

    return {
      videos: filteredVideos,
      totalCount: videos.length,
      filteredCount: filteredVideos.length,
      availableTags,
    };
  }, [videos, tagFilters, availableTags]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(tagFilters).some((filterSet) => filterSet.size > 0);
  }, [tagFilters]);

  return {
    tagFilters,
    filterResult,
    toggleTagValue,
    clearAllFilters,
    clearTagKeyFilters,
    hasActiveFilters,
    availableTags,
  };
}
